import { useMocks } from '../../shared/env';
import { peopleGateway, listAllEmployees } from '../people';
import { EmploymentStatus, type EmployeeResponse } from '../people/types';

/**
 * The company's employees as this feature needs them: a name to show, and the two facts Phase 1 eligibility rests on.
 *
 * PHASE 1 ELIGIBILITY MEANS ACTIVE EMPLOYEES. There is no eligibility-rule engine yet (no hours, class, location or waiting-period rules), so
 * "eligible" is every employee whose status is Active. The Benefits Overview says so on the page. Names come from the Employees API
 * (`peopleGateway`), never from the benefit or deduction services.
 */
export interface DirectoryEmployee {
  id: string;
  /** `Last, First`, for sorting and pickers. */
  name: string;
  employeeNumber: string;
  /** The Employees API status (0 Active, 1 On leave, 2 Terminated). */
  status: number;
  hireDate: string;
}

export function isActiveEmployee(e: Pick<DirectoryEmployee, 'status'>): boolean {
  return e.status === EmploymentStatus.Active;
}

function toDirectory(e: EmployeeResponse): DirectoryEmployee {
  return { id: e.id, name: `${e.firstName} ${e.lastName}`.trim(), employeeNumber: e.employeeNumber, status: e.status, hireDate: e.hireDate };
}

/** SYNTHETIC people for the mock demo company (contract section 7): never real people, never loaded outside the Mock gateways. One is a future hire and
 * one is terminated, so the bulk preview can show its exclusion reasons. */
export const SYNTHETIC_EMPLOYEES: readonly DirectoryEmployee[] = [
  { id: 'syn-emp-01', name: 'SYNTHETIC Employee 01', employeeNumber: 'SYN-01', status: EmploymentStatus.Active, hireDate: '2024-01-08' },
  { id: 'syn-emp-02', name: 'SYNTHETIC Employee 02', employeeNumber: 'SYN-02', status: EmploymentStatus.Active, hireDate: '2024-03-04' },
  { id: 'syn-emp-03', name: 'SYNTHETIC Employee 03', employeeNumber: 'SYN-03', status: EmploymentStatus.Active, hireDate: '2025-02-10' },
  { id: 'syn-emp-04', name: 'SYNTHETIC Employee 04', employeeNumber: 'SYN-04', status: EmploymentStatus.Active, hireDate: '2025-09-01' },
  { id: 'syn-emp-05', name: 'SYNTHETIC Employee 05 (future hire)', employeeNumber: 'SYN-05', status: EmploymentStatus.Active, hireDate: '2099-01-04' },
  { id: 'syn-emp-06', name: 'SYNTHETIC Employee 06 (terminated)', employeeNumber: 'SYN-06', status: EmploymentStatus.Terminated, hireDate: '2022-05-02' },
];

/** Every employee in the company, via the Employees API. In a mock-backed build the synthetic demo employees are added, so the demo elections have
 * names; the real build never adds anything. */
export async function loadEmployeeDirectory(): Promise<DirectoryEmployee[]> {
  const all = await listAllEmployees(peopleGateway);
  const people = all.map(toDirectory);
  const merged = useMocks ? [...people, ...SYNTHETIC_EMPLOYEES.filter((s) => !people.some((p) => p.id === s.id))] : people;
  return merged.sort((a, b) => a.name.localeCompare(b.name));
}
